import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { User, UserCredential, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, signOut, ActionCodeSettings, updatePassword } from 'firebase/auth';
import { UsuarioService } from './usuario.service';
import { NetworkService } from './network.service';
import { firstValueFrom, timeout, catchError, of, Observable } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { CapacitorHttp } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';
import { NotificacionService } from './notificacion.service';
import { AnalyticsService } from './analytics.service';
import { OrderComercioService } from './order-comercio.service';
import { OrderPrestadorService } from './order-prestador.service';
import { LoadingService } from './loading.service';
import { API_URI, environment } from 'src/environments/environment';

type TokenBundle = {
  idToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
  localId: string;  // uid
};

export type LoginResult =
  | { ok: true }
  | { ok: false; reason: 'email-not-verified' };

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Auth opcional (en nativo no se provee)
  private auth: Auth | null = inject(Auth, { optional: true }) || null;
  private http = inject(HttpClient);
  private router = inject(Router);
  private _loadingService = inject(LoadingService);
  private _network = inject(NetworkService);
  private _usuarioService = inject(UsuarioService);
  private _notificacionService = inject(NotificacionService);
  private _analytics = inject(AnalyticsService);
  private _orderComercioService = inject(OrderComercioService);
  private _orderPrestadorService = inject(OrderPrestadorService);
  private readonly API_URI: string = API_URI + 'app';
  private sleep(ms: number) { return new Promise(res => setTimeout(res, ms)); }

  // === Config Firebase API Key ===
  private readonly FIREBASE_API_KEY: string =
    (environment as any)?.firebase?.apiKey ?? (environment as any)?.firebaseApiKey;

  // Estado
  private BIOMETRIC_EMAIL_KEY = 'biometricEmail';
  private BIOMETRIC_PASS_KEY  = 'biometricPassword';
  private BIOMETRIC_ENABLED   = 'biometricEnabled'; 
  currentUser  = signal<User | null>(null);
  session      = signal<'guest'|'web'|'native'>('guest'); // <- unificado
  authReady    = signal(false);                            // <- bootstrap listo
  sessionError = signal<'offline' | 'data-load-failed' | null>(null);

  // ---- Derivados cómodos ----
  isLoggedIn = computed(() => this.session() !== 'guest');
  isVerified = computed(() => {
    if (!this.isLoggedIn()) return false;
    if (this.session() === 'web') return !!this.currentUser()?.emailVerified;
    // nativo: persistimos flag
    return localStorage.getItem('emailVerified') === '1';
  });

  // Web: primera resolución del SDK
  private _firstAuth?: Promise<User|null>;
  private waitForAuthInit(): Promise<User | null> {
    // si no hay Auth (nativo), resolvemos a null
    if (!this.auth) return Promise.resolve(null);

    if (!this._firstAuth) {
      this._firstAuth = new Promise(resolve => {
        const off = onAuthStateChanged(this.auth!, u => { off(); resolve(u); });
      });
    }
    return this._firstAuth;
  }

  // helper para evitar errores si algún día activas SSR
  // Helpers storage/browser-safe
  private hasLocalStorage(): boolean {
    try {
      return typeof window !== 'undefined' && !!window.localStorage;
    } catch {
      return false;
    }
  }

  constructor() {
    // Solo refleja el user del SDK (útil en WEB). En nativo puede quedar null.
    // reflejar user del SDK SOLO en web (cuando _authService existe)
    if (this.auth && this.isNative() === false) {
      onAuthStateChanged(this.auth, (u) => {
        this.currentUser.set(u);
        this.session.set(u ? 'web' : 'guest');
      });
    }
  }

  // ======= Plataforma / Tokens (para Nativo) =======

  private isNative(): boolean {
    return Capacitor.getPlatform() !== 'web';
  }
  private isIOS = (Capacitor.getPlatform?.() === 'ios');

  // helper por si ensureSession se invoca temprano en nativo
  private async waitForWebViewReady(timeoutMs = 4000): Promise<void> {
    if (!this.isNative()) return;
    // Si ya está completo, listo
    if (document.readyState === 'complete') {
      await new Promise(r => setTimeout(r, 0));
      return;
    }
    await Promise.race<void>([
      // 1) Si load ya pasó antes, este puede no disparar, pero lo dejamos igual
      new Promise<void>(res =>
        window.addEventListener('load', () => res(), { once: true })
      ),

      // 2) DOMContentLoaded suele ser más confiable que load
      new Promise<void>(res =>
        document.addEventListener('DOMContentLoaded', () => res(), { once: true })
      ),

      // 3) Polling por si los eventos no llegan (muy útil en algunos WebView)
      (async () => {
        const start = Date.now();
        while (Date.now() - start < timeoutMs) {
          if (document.readyState === 'interactive' || document.readyState === 'complete') return;
          await new Promise(r => setTimeout(r, 50));
        }
        // si se vence, salimos igual (no bloqueamos el arranque)
      })(),

      // 4) Timeout duro para garantizar que NUNCA se cuelgue
      new Promise<void>(res => setTimeout(() => res(), timeoutMs)),
    ]);

    // Micro-turno para evitar un frame raro
    await new Promise(r => setTimeout(r, 0));
  }

  private async getStoredFbTokens(): Promise<TokenBundle | null> {
    const v = await Preferences.get({ key: 'fbTokens' });
    return v.value ? JSON.parse(v.value) as TokenBundle : null;
  }

  private async setStoredFbTokens(t: TokenBundle): Promise<void> {
    await Preferences.set({ key: 'fbTokens', value: JSON.stringify(t) });
  }

  async currentIdToken(): Promise<string | null> {
    if (!this.isNative()) {
      // proteger acceso si _authService es null (no debería en web)
      const u = this.auth?.currentUser || null;
      return u ? await u.getIdToken() : null;
    }
    const t = await this.getStoredFbTokens();
    if (!t) return null;
    if (Date.now() >= t.expiresAt) {
      const r = await this.fbRefreshNative(t.refreshToken);
      await this.setStoredFbTokens(r);
      localStorage.setItem('tokenId', r.idToken); // compat con tu lógica actual
      return r.idToken;
    }
    return t.idToken;
  }

  // ========= Helpers nativos con retry y HTTP/1.1 en iOS =========
  // Forzar HTTP/1.1 en iOS y reintentar errores de red transitorios
  private async withRetry<T>(fn: () => Promise<T>, retries = 2, delayMs = 300): Promise<T> {
    try { return await fn(); }
    catch (e: any) {
      const code = (e?.code || e?.message || e?.errorMessage || '').toString();
      const transient =
        code.includes('NSURLErrorDomain') ||
        /network.*lost/i.test(code) ||
        /timed\s*out/i.test(code);
      if (retries > 0 && transient) {
        await new Promise(r => setTimeout(r, delayMs));
        return this.withRetry(fn, retries - 1, delayMs * 2);
      }
      throw e;
    }
  }

  private async postJsonNative<T = any>(url: string, data: any) {
    const headers: any = { 'Content-Type': 'application/json', 'Referer': 'https://app.libresalud.com.ar' };
    if (this.isIOS) headers['Connection'] = 'close';
    return this.withRetry(async () => {
      const res = await CapacitorHttp.post({ url, headers, data });
      if (res.status !== 200) throw new Error(JSON.stringify(res.data));
      return res.data as T;
    });
  }

  private async postFormNative<T = any>(url: string, body: URLSearchParams) {
    const headers: any = { 'Content-Type': 'application/x-www-form-urlencoded', 'Referer': 'https://app.libresalud.com.ar' };
    if (this.isIOS) headers['Connection'] = 'close';
    return this.withRetry(async () => {
      const res = await CapacitorHttp.post({ url, headers, data: body.toString() });
      if (res.status !== 200) throw new Error(JSON.stringify(res.data));
      return res.data as T;
    });
  }

  // ======= Firebase REST para Nativo (sin CORS) =======

  private async fbSignInNative(email: string, password: string): Promise<TokenBundle> {
    const url = `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${this.FIREBASE_API_KEY}`;
    const data = await this.postJsonNative<{ idToken: string; refreshToken: string; expiresIn: string; localId: string; }>(
      url, { email, password, returnSecureToken: true }
    );
    const { idToken, refreshToken, expiresIn, localId } = data;
    return { idToken, refreshToken, localId, expiresAt: Date.now() + (Number(expiresIn) - 60) * 1000 };
  }

  private async fbSignUpNative(email: string, password: string): Promise<TokenBundle> {
    const url = `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${this.FIREBASE_API_KEY}`;
    const data = await this.postJsonNative<{ idToken: string; refreshToken: string; expiresIn: string; localId: string; }>(
      url, { email, password, returnSecureToken: true }
    );
    const { idToken, refreshToken, expiresIn, localId } = data;
    return { idToken, refreshToken, localId, expiresAt: Date.now() + (Number(expiresIn) - 60) * 1000 };
  }

  private async fbSendVerifyEmailNative(idToken: string, continueUrl?: string): Promise<void> {
    const url = `https://identitytoolkit.googleapis.com/v1/accounts:sendOobCode?key=${this.FIREBASE_API_KEY}`;
    const data: any = {
      requestType: 'VERIFY_EMAIL',
      idToken,
      // retorno a libresalud
      continueUrl,
      // para verificación, primero web y luego retorno a app si corresponde
      canHandleCodeInApp: false,
    };
    await this.postJsonNative(url, data);
  }

  private async fbSendPasswordResetNative(email: string, continueUrl?: string): Promise<void> {
    const url = `https://identitytoolkit.googleapis.com/v1/accounts:sendOobCode?key=${this.FIREBASE_API_KEY}`;
    const data: any = {
      requestType: 'PASSWORD_RESET',
      email,
      // retorno a libresalud
      continueUrl,
    };
    await this.postJsonNative(url, data);
  }

  private async fbLookupEmailVerified(idToken: string): Promise<boolean> {
    const { emailVerified } = await this.fbLookupUser(idToken);
    return emailVerified;
  }

  private async fbLookupUser(idToken: string): Promise<{ emailVerified: boolean; email: string }> {
    const url = `https://identitytoolkit.googleapis.com/v1/accounts:lookup?key=${this.FIREBASE_API_KEY}`;
    const data = await this.postJsonNative<{ users?: Array<{ emailVerified?: boolean; email?: string }> }>(
      url, { idToken }
    );
    return {
      emailVerified: !!data?.users?.[0]?.emailVerified,
      email: data?.users?.[0]?.email || '',
    };
  }

  private async fbRefreshNative(refreshToken: string): Promise<TokenBundle> {
    const url = `https://securetoken.googleapis.com/v1/token?key=${this.FIREBASE_API_KEY}`;
    const body = new URLSearchParams({ grant_type: 'refresh_token', refresh_token: refreshToken });
    const data = await this.postFormNative<{ id_token: string; refresh_token: string; expires_in: string; user_id: string }>(
      url, body
    );
    const { id_token, refresh_token, expires_in, user_id } = data;
    return {
      idToken: id_token, refreshToken: refresh_token, localId: user_id,
      expiresAt: Date.now() + (Number(expires_in) - 60) * 1000
    };
  }

  private async fbUpdatePasswordNative(idToken: string, newPassword: string): Promise<TokenBundle> {
    const url = `https://identitytoolkit.googleapis.com/v1/accounts:update?key=${this.FIREBASE_API_KEY}`;
    const data = await this.postJsonNative<{ idToken: string; refreshToken: string; expiresIn: string; localId: string }>(
      url, { idToken, password: newPassword, returnSecureToken: true }
    );
    const { idToken: newIdToken, refreshToken, expiresIn, localId } = data;
    return {
      idToken: newIdToken, refreshToken, localId,
      expiresAt: Date.now() + (Number(expiresIn) - 60) * 1000
    };
  }

  // ======= Sesión / Bootstrap =======

  private withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
    return Promise.race([
      p,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error(`${label} timeout (${ms}ms)`)), ms)
      ),
    ]);
  }

  // Según id_perfil, la entidad "propia" del usuario logueado es distinta:
  // 1 Administrador y 2 Cliente comparten el registro de clientes, 3 es
  // Prestador, 4 es Comercio. Verifica que esa entidad puntual haya quedado
  // realmente cargada (id presente), no alcanza con que la llamada no haya
  // tirado error.
  private perfilDataLoaded(): boolean {
    const u = this._usuarioService.usuario();
    switch (u?.id_perfil) {
      case 1: // ADMIN => mismo registro que CLIENTE
      case 2: // CLIENTE
        return !!this._usuarioService.cliente()?.id;
      case 3: // PRESTADOR
        return !!this._usuarioService.prestador()?.id;
      case 4: // COMERCIO
        return !!this._usuarioService.comercio()?.id;
      default:
        return true; // perfil no reconocido: nada puntual que verificar
    }
  }

  // Carga prestador/comercio/cliente según el perfil. Reintenta una vez ante
  // fallos transitorios (red, timeout, o una respuesta que no dejó la señal
  // cargada) antes de resignarse, porque si falla definitivo la señal queda
  // "pegada" en su valor por defecto (id: '') durante toda la sesión, y de
  // eso dependen otras pantallas (dashboard, pedidos, etc.), no solo la de
  // editar perfil.
  private async ensureUsuarioXPerfil(): Promise<boolean> {
    const intentar = async (label: string): Promise<boolean> => {
      try {
        await this.withTimeout(this._usuarioService.createUsuarioXPerfil(), 7000, label);
      } catch {
        return false;
      }
      return this.perfilDataLoaded();
    };

    if (await intentar('createUsuarioXPerfil')) return true;

    await this.sleep(800);
    return intentar('createUsuarioXPerfil (retry)');
  }

  private _ensureOnce?: Promise<void>;
  ensureSession(): Promise<void> {
    if (this._ensureOnce) return this._ensureOnce;
    // si no hay internet, no arrancamos flujos de red
    if (!this._network.isOnline()) {
      console.warn('Sin conexión a internet');
      // No borrar tokens: podrían ser válidos cuando vuelva la red
      this.currentUser.set(null);
      this.session.set('guest');
      this.sessionError.set('offline');
      this.authReady.set(true);
      return Promise.resolve();
      // _ensureOnce no se asigna → próxima llamada reintentará automáticamente
    }

    this._ensureOnce = this._loadingService.run(async () => {
      try {
        // si es nativo y alguien lo llama temprano, esperamos WebView
        // 1) WebView ready con timeout
        await this.withTimeout(this.waitForWebViewReady(), 4000, 'waitForWebViewReady');

        if (this.isNative()) {
          // Rehidrata tokens nativos con timeout (no hay user SDK)
          const t = await this.withTimeout(this.getStoredFbTokens(), 3000, 'getStoredFbTokens')
          .catch(() => null);
          if (!t) {
            this.clearLocalSession();
            this.currentUser.set(null);
            this.session.set('guest');
            this.authReady.set(true);
            return;
          }
          const idToken = await this.withTimeout(this.currentIdToken(), 5000, 'currentIdToken')
          .catch(() => '');
          if (!idToken) {
            this.clearLocalSession();
            this.currentUser.set(null);
            this.session.set('guest');
            this.authReady.set(true);
            return;
          }

          // 3) emailVerified: no bloquear por red
          try {
            // Si no tenemos flag de verificación, lo consultamos 1 vez
            if (!localStorage.getItem('emailVerified')) {
              const v = await this.withTimeout(
                this.fbLookupEmailVerified(idToken).catch(() => false),
                5000,
                'fbLookupEmailVerified'
              ).catch(() => false);
              localStorage.setItem('emailVerified', v ? '1' : '0');
            }
          } catch {
            // si falla, no bloqueamos el arranque
            localStorage.setItem('emailVerified', '0');
          }
          localStorage.setItem('tokenId', idToken); // para interceptor

          // Refresca también el JWT propio del backend (no solo el idToken de
          // Firebase): si el que había en storage quedó inválido (p.ej. por
          // una rotación de SECRET_KEY), sin esto la sesión queda "logueada"
          // para Firebase pero todos los pedidos al backend fallan con 401 en
          // silencio (nunca cargan notificaciones, datos de usuario, etc.).
          const backendResNative = await this.withTimeout(firstValueFrom(this.loginBackend(idToken)), 7000, 'loginBackend')
            .catch(() => null);
          const tokenBackendNative = this.extractBackendToken(backendResNative);
          if (tokenBackendNative) localStorage.setItem('tokenBackend', tokenBackendNative);

          // Recuperar email si fue borrado previamente (p.ej. por clearLocalSession offline)
          let email = localStorage.getItem('email') || '';
          if (!email) {
            try {
              const userInfo = await this.withTimeout(this.fbLookupUser(idToken), 5000, 'fbLookupUser').catch(() => null);
              if (userInfo?.email) {
                email = userInfo.email;
                localStorage.setItem('email', email);
                localStorage.setItem('emailVerified', userInfo.emailVerified ? '1' : '0');
              }
            } catch { /* si falla, email sigue vacío */ }
          }

          if (email) {
            // HTTP con timeout para que no quede colgado
            let u: any = null;
            let notFound = false;
            try {
              u = await firstValueFrom(
                this._usuarioService.getUsuarioXEmail(email).pipe(timeout(7000))
              );
            } catch (err: any) {
              notFound = err?.status === 404;
            }

            if (!u) {
              if (notFound) {
                // La cuenta ya no existe en el backend (borrada, etc.): la sesión
                // vieja guardada en el dispositivo no sirve. Se limpia en silencio
                // y se muestra el login vacío — "Reintentar" nunca lo arreglaría.
                this.clearLocalSession();
                this.currentUser.set(null);
                this.session.set('guest');
                this.authReady.set(true);
                return;
              }
              // Backend no respondió (timeout o error de red): no mostrar app vacía
              this._ensureOnce = undefined; // permite reintento al navegar
              this.session.set('guest');
              this.sessionError.set('data-load-failed');
              this.authReady.set(true);
              return;
            }

            this._usuarioService.usuario.set(u);

            const perfilCargadoNative = await this.ensureUsuarioXPerfil();
            if (!perfilCargadoNative) {
              // No se pudo cargar cliente/prestador/comercio ni reintentando.
              // NO marcamos la sesión como lista: usuario() quedaría bien pero
              // cliente() vacío, y pantallas que dependen de cliente().id
              // (identidad, el botón de emergencia) actuarían con datos a
              // medias sin que nadie se entere — exactamente el bug de
              // "sesión abierta pero sin datos cargados".
              this._ensureOnce = undefined; // permite reintento al navegar
              this.session.set('guest');
              this.sessionError.set('data-load-failed');
              this.authReady.set(true);
              return;
            }

            // Notificaciones: esto también tiene que pasar acá, no solo en
            // login(), porque la app casi siempre arranca restaurando una
            // sesión guardada (sin volver a loguearse) — si no, nunca se
            // (re)registra el token de push en esas aperturas.
            if (u?.id) {
              this._notificacionService.obtenerNotificaciones();
              this._notificacionService.initNotificaciones();
              this._notificacionService.verificarEstadoNotificaciones(u.id);
            }

            if (u?.id) {
              await this.withTimeout(this._usuarioService.getUbicacionDefault(u.id), 7000, 'getUbicacionDefault')
                .catch(() => undefined);
            }

            void this._analytics.setUser(t.localId ?? null);
            this.session.set('native');
            this.authReady.set(true);
            return;
          }

          // Sin email disponible: no se puede identificar al usuario
          this._ensureOnce = undefined;
          this.session.set('guest');
          this.sessionError.set('data-load-failed');
          this.authReady.set(true);
          return;
        }

        // WEB: tu flujo original
        const user = await this.withTimeout(this.waitForAuthInit(), 8000, 'waitForAuthInit')
        .catch(() => null);
        const remember = localStorage.getItem('recordarme') === 'true';

        if (!user) {
          // Sin sesión Firebase
          this.clearLocalSession();
          this.currentUser.set(null);
          this.session.set('guest');
          this.authReady.set(true);
          return;
        }

        // Si querés exigir "recordarme" para persistir entre arranques:
        if (!remember) {
          // si el usuario existe y NO está verificado, lo dejamos en sesión
          // para que pueda ir a /verify-email (y reenviar/recargar verificación)
          if (user && !user.emailVerified) {
            this.currentUser.set(user);
            this.session.set('web');
            localStorage.setItem('emailVerified', '0');
            this.authReady.set(true);
            return;
          }
          // si está verificado pero no quiso "recordarme", sí lo deslogueamos
          // proteger signOut si por algún motivo _authService es null
          if (this.auth) await this.withTimeout(signOut(this.auth), 5000, 'signOut').catch(() => undefined);
          this.clearLocalSession();
          this.currentUser.set(null);
          this.session.set('guest');
          this.authReady.set(true);
          return;
        }

        if (!user.emailVerified) {
          this.currentUser.set(user);
          this.session.set('web');
          this.authReady.set(true);
          return;
        }

        // Refresca idToken y guarda en storage (si tu interceptor lo usa)
        const idToken = await this.withTimeout(user.getIdToken(true), 7000, 'getIdToken')
        .catch(() => '');
        if (idToken) localStorage.setItem('tokenId', idToken);
        if (user.email) localStorage.setItem('email', user.email);

        // Refresca también el JWT propio del backend (ver comentario en la
        // rama nativa de este mismo método).
        if (idToken) {
          const backendResWeb = await this.withTimeout(firstValueFrom(this.loginBackend(idToken)), 7000, 'loginBackend')
            .catch(() => null);
          const tokenBackendWeb = this.extractBackendToken(backendResWeb);
          if (tokenBackendWeb) localStorage.setItem('tokenBackend', tokenBackendWeb);
        }

        // Rehidrata datos de backend
        let u: any = null;
        let notFound = false;
        try {
          u = await firstValueFrom(
            this._usuarioService.getUsuarioXEmail(user.email!).pipe(timeout(7000))
          );
        } catch (err: any) {
          notFound = err?.status === 404;
        }

        if (!u) {
          if (notFound) {
            // La cuenta ya no existe en el backend: la sesión de Firebase vieja
            // no sirve. Se limpia en silencio y se muestra el login vacío —
            // "Reintentar" nunca lo arreglaría.
            if (this.auth) await this.withTimeout(signOut(this.auth), 5000, 'signOut').catch(() => undefined);
            this.clearLocalSession();
            this.currentUser.set(null);
            this.session.set('guest');
            this.authReady.set(true);
            return;
          }
          // Backend no respondió: no mostrar app vacía
          this._ensureOnce = undefined; // permite reintento
          this.currentUser.set(user);
          this.session.set('guest');
          this.sessionError.set('data-load-failed');
          this.authReady.set(true);
          return;
        }

        this._usuarioService.usuario.set(u);

        const perfilCargadoWeb = await this.ensureUsuarioXPerfil();
        if (!perfilCargadoWeb) {
          // No se pudo cargar cliente/prestador/comercio ni reintentando. NO
          // marcamos la sesión como lista: usuario() quedaría bien pero
          // cliente() vacío, y pantallas que dependen de cliente().id
          // (identidad, el botón de emergencia) actuarían con datos a medias
          // sin que nadie se entere.
          this._ensureOnce = undefined; // permite reintento al navegar
          this.currentUser.set(user);
          this.session.set('guest');
          this.sessionError.set('data-load-failed');
          this.authReady.set(true);
          return;
        }

        // Notificaciones: esto también tiene que pasar acá, no solo en
        // login(), porque la app casi siempre arranca restaurando una
        // sesión guardada (sin volver a loguearse) — si no, nunca arranca
        // el polling que actualiza el puntito de notificación nueva.
        if (u?.id) {
          this._notificacionService.obtenerNotificaciones();
          this._notificacionService.initNotificaciones();
          this._notificacionService.verificarEstadoNotificaciones(u.id);
        }

        if (u?.id) {
          await this.withTimeout(this._usuarioService.getUbicacionDefault(u.id), 7000, 'getUbicacionDefault')
            .catch(() => undefined);
        }

        void this._analytics.setUser(user.uid);
        this.currentUser.set(user);
        this.session.set('web');
        this.authReady.set(true);
      } catch (e) {
        console.warn('ensureSession falló (pero no bloquea arranque):', e);
        // No borrar tokens: el error puede ser de red, no de auth inválido
        this._ensureOnce = undefined; // permite reintento en la próxima navegación
        this.currentUser.set(null);
        this.session.set('guest');
        this.sessionError.set('data-load-failed');
      } finally {
        // SIEMPRE
        this.authReady.set(true);
      }
    });

    return this._ensureOnce;
  }

  // ======= Registro / Login / Logout =======
  async register(email: string, clave: string, idPerfil: number, nroSocio: string): Promise<void> {
    await this._loadingService.run(async () => {
      if (!this.isNative()) {
        // WEB → SDK
        // proteger si _authService no está (no debería pasar en web)
        if (!this.auth) throw new Error('Firebase Auth no disponible en esta plataforma.');
        
        // 1) Crear usuario en Firebase
        const cred: UserCredential = await createUserWithEmailAndPassword(this.auth, email, clave);

        // 2) Registrar en tu backend (Authorization: Bearer <idToken>)
        await firstValueFrom(this.registerBackend(cred.user, clave, idPerfil, nroSocio));

        // 3) Enviar email de verificación. Si esto falla (p.ej. un error
        // transitorio del propio backend de Firebase, tipo "Error code: 26" /
        // backendError), la cuenta YA quedó creada (Firebase + nuestro
        // backend) — no tiene sentido tirar todo el registro como fallido: el
        // usuario reintentaría y chocaría con EMAIL_EXISTS sin poder
        // verificar la cuenta que ya tiene. Puede reenviarlo desde /verify-email.
        try {
          await this.verificarEmail(cred.user);
        } catch (e) {
          console.warn('No se pudo enviar el email de verificación al registrar (se puede reenviar después):', e);
        }

        return;
      }
      // NATIVO → REST
      const t = await this.fbSignUpNative(email, clave);
      await this.setStoredFbTokens(t);
      localStorage.setItem('tokenId', t.idToken);
      localStorage.setItem('email', email);
      localStorage.removeItem('emailVerified'); // aún no verificado

      await firstValueFrom(this.registerBackend({ uid: t.localId, email } as any, clave, idPerfil, nroSocio));
      try {
        await this.fbSendVerifyEmailNative(t.idToken, `https://libresalud-51213.web.app/verify-email`);
      } catch (e) {
        console.warn('No se pudo enviar el email de verificación al registrar (se puede reenviar después):', e);
      }
    });
  }

  async login(email: string, clave: string): Promise<LoginResult> {
      if (!this.isNative()) {
        // WEB → SDK
        // proteger si _authService no está (no debería pasar en web)
        if (!this.auth) throw new Error('Firebase Auth no disponible en esta plataforma.');

        // 1) Firebase sign-in
        const cred: UserCredential = await signInWithEmailAndPassword(this.auth, email, clave);
        const user = cred.user;

        // 2) Verificación de email
        if (!user.emailVerified) {
          console.log("Email no verificado");
          this.session.set('web');
          return { ok: false, reason: 'email-not-verified' };
        }

        // 3) ID token de Firebase (para tu app / por si lo necesitas)
        const idToken = await user.getIdToken(true);

        // 4) Login en tu backend (flujo actual con email+clave)
        const backendRes = await firstValueFrom(this.loginBackend(idToken));
        const tokenBackend = this.extractBackendToken(backendRes);

        // 5) Persistir tokens (para guard/interceptor)
        this.saveToken(idToken, email, tokenBackend);

        // 6) Cargar datos del usuario de tu backend
        const usuario = await firstValueFrom(this._usuarioService.getUsuarioXEmail(email));
        if (!usuario?.b_activo) {
          throw { code: 'auth/inactive-user' };
        }
        this._usuarioService.usuario.set(usuario);
        await this._usuarioService.createUsuarioXPerfil();

        // 7) Notificaciones + datos complementarios
        this._notificacionService.obtenerNotificaciones();
        this._notificacionService.initNotificaciones();
        this._notificacionService.verificarEstadoNotificaciones(usuario.id!);
        this._usuarioService.getUbicacionDefault(usuario.id!);

        // 8) Navegar al dashboard
        void this._analytics.setUser(user.uid);
        void this._analytics.logEvent('login', { method: 'password' });
        this.session.set('web');
        return { ok: true };
      }

      // NATIVO → REST
      const t = await this.fbSignInNative(email, clave);
      const emailVerified = await this.fbLookupEmailVerified(t.idToken);

      if (!emailVerified) {
        // no persistir sesión
        await Preferences.remove({ key: 'fbTokens' });
        this.clearLocalSession();

        localStorage.setItem('emailVerified', '0');
        localStorage.setItem('pendingEmail', email); // opcional

        this.session.set('native');

        return { ok: false, reason: 'email-not-verified' };
      }

      await this.setStoredFbTokens(t);
      localStorage.setItem('tokenId', t.idToken);
      localStorage.setItem('email', email);
      localStorage.setItem('emailVerified', emailVerified ? '1' : '0');

      const backendRes = await firstValueFrom(this.loginBackend(t.idToken));
      const tokenBackend = this.extractBackendToken(backendRes);
      this.saveToken(t.idToken, email, tokenBackend);

      const usuario = await firstValueFrom(this._usuarioService.getUsuarioXEmail(email));
      if (!usuario?.b_activo) throw { code: 'auth/inactive-user' };

      this._usuarioService.usuario.set(usuario);
      await this._usuarioService.createUsuarioXPerfil();
      // Notificaciones + datos complementarios
      this._notificacionService.obtenerNotificaciones();
      this._notificacionService.initNotificaciones();
      this._notificacionService.verificarEstadoNotificaciones(usuario.id!);
      this._usuarioService.getUbicacionDefault(usuario.id!);
      // Navegar al dashboard
      void this._analytics.setUser(t.localId);
      void this._analytics.logEvent('login', { method: 'password' });
      this.session.set('native');
      return { ok: true };
  }

  async retrySession(): Promise<void> {
    this._ensureOnce = undefined;
    this.sessionError.set(null);
    this.authReady.set(false);
    await this._network.refresh();
    await this.ensureSession();
  }

  async logout(): Promise<void> {
    await this._loadingService.run(async () => {
      await this.sleep(1500);
      if (!this.isNative()) {
        // proteger signOut
        if (this.auth) await signOut(this.auth);          // cierra sesión de Firebase WEB
      }
      await Preferences.remove({ key: 'fbTokens' }); // NATIVO
      // revocar el token de push de este dispositivo ANTES de limpiar el token de auth
      await this._notificacionService.revokeCurrentDeviceToken().catch(() => undefined);
      this.clearLocalSession();          // limpia storage y caches
      void this._analytics.setUser(null);
      this.currentUser.set(null);        // actualiza signal
      this.session.set('guest');
      this.authReady.set(true);
      await this.router.navigateByUrl('/sign-in'); // redirige
    });
  }

  async logoutFast(): Promise<void> {
    if (!this.isNative()) {
      if (this.auth) await signOut(this.auth);
    }
    await Preferences.remove({ key: 'fbTokens' });
    await this._notificacionService.revokeCurrentDeviceToken().catch(() => undefined);
    this.clearLocalSession();
    void this._analytics.setUser(null);
    this.currentUser.set(null);
    this.session.set('guest');
    this.authReady.set(true);
  }

  // ======= Acciones de cuenta =======

  async verificarEmail(user?: User): Promise<void> {
    const redirectUrl = 'https://app.libresalud.com.ar';

    if (!this.isNative()) {
      const u = this.auth?.currentUser || null;
      if (!u) throw new Error('No hay usuario autenticado para verificar.');

      const actionCodeSettings: ActionCodeSettings = {
        url: redirectUrl,
        handleCodeInApp: false,
      };

      await sendEmailVerification(u, actionCodeSettings);
      return;
    }
    const t = await this.getStoredFbTokens();
    if (!t?.idToken) throw new Error('No hay sesión nativa para verificar.');
    await this.fbSendVerifyEmailNative(t.idToken, redirectUrl);
  }

  async recoverPassword(email: string): Promise<void> {
    await this._loadingService.run(async () => {
      const redirectUrl = 'https://app.libresalud.com.ar/sign-in';

      if (!this.isNative()) {
        if (!this.auth) throw new Error('Firebase Auth no disponible.');
        await sendPasswordResetEmail(this.auth, email, {
          url: redirectUrl,
          handleCodeInApp: false,
        })
        return;
      }
      await this.fbSendPasswordResetNative(email, redirectUrl);
    });
  }

  async actualizarClaveCurrentUser(newPassword: string): Promise<void> {
    await this._loadingService.run(async () => {
      if (!this.isNative()) {
        const u = this.auth?.currentUser || null;
        if (!u) throw new Error('No hay usuario autenticado.');
        await updatePassword(u, newPassword);
        return;
      }
      const t = await this.getStoredFbTokens();
      if (!t?.idToken) throw new Error('No hay sesión nativa.');
      const r = await this.fbUpdatePasswordNative(t.idToken, newPassword);
      await this.setStoredFbTokens(r);
      localStorage.setItem('tokenId', r.idToken);
    });
  }

  // ======= Backend helpers =======
  private registerBackend(user: User, xPassword: string, idPerfil: number, nroSocio: string): Observable<any>{
    const body = {
      id: user.uid,
      x_email: user.email,
      x_password: xPassword,
      id_perfil: idPerfil,
      x_nro_socio: nroSocio
    }
    return this.http.post<string>(`${this.API_URI}/usuarios/register`, body);
  }

  private loginBackend(idToken: string): Observable<any> {
    const body = { idToken };
    return this.http.post<string | { token?: string; access_token?: string }>(
      `${this.API_URI}/usuarios/login`,
      body
    );
  }

  private extractBackendToken(res: any): string {
    if (typeof res === 'string') return res;
    return res?.token ?? res?.access_token ?? '';
  }

  private saveToken(tokenId: string, email: string, tokenBackend: string): void {
    localStorage.setItem('tokenId', tokenId);
    localStorage.setItem('email', email);
    localStorage.setItem('tokenBackend', tokenBackend);
  }

  private clearLocalSession(): void {
    if (this.hasLocalStorage()) {
      const keys = [
        'tokenId',
        'email',
        'emailVerified',
        'tokenBackend',
        'modalPwaPlatform',
        'recordarme',
        'notificacionesPushRegistradas',
        'showNotifications',
        //this.BIOMETRIC_FLAG, // por si activaste biometría
      ];
      keys.forEach(k => localStorage.removeItem(k));
    }

    // limpia caches de otros servicios si existen
    this._usuarioService?.clearData?.();
    this._notificacionService?.clearData?.();
    this._orderComercioService?.clearData?.();
    this._orderPrestadorService?.clearData?.();
  }

  // ======= Recordarme / Biometría =======

  // === Recordarme (opcional) ===
  setRememberMe(email: string | null, remember: boolean) {
    if (remember && email) {
      localStorage.setItem('recordarme', 'true');
      localStorage.setItem('email', email);
    } else {
      localStorage.removeItem('recordarme');
      localStorage.removeItem('email');
    }
  }

  getRememberedEmail(): string | null {
    return localStorage.getItem('recordarme') === 'true'
      ? localStorage.getItem('email')
      : null;
  }

  // === Biometría ===
  // Guardar credenciales protegidas por biometría (se llama 1 vez tras login normal)
  async saveBiometricCredentials(email: string, password: string): Promise<void> {
    if (!this.isNative()) return;
    const info = await BiometricAuth.checkBiometry().catch(() => ({ isAvailable: false }));
    if (!info?.isAvailable) throw new Error('Biometría no disponible');

    // Pedí confirmación biométrica ANTES de guardar
    await BiometricAuth.authenticate({
      reason: 'Guardar credenciales seguras',
      iosFallbackTitle: 'Usar código',
      cancelTitle: 'Cancelar',
      allowDeviceCredential: true,
    });

    await SecureStoragePlugin.set({ key: this.BIOMETRIC_EMAIL_KEY, value: email });
    await SecureStoragePlugin.set({ key: this.BIOMETRIC_PASS_KEY,  value: password });
    localStorage.setItem(this.BIOMETRIC_ENABLED, '1');
  }

  // ¿Hay credenciales guardadas?
  async hasBiometricCredentials(): Promise<boolean> {
    if (!this.isNative()) return false;
    try {
      await SecureStoragePlugin.get({ key: this.BIOMETRIC_EMAIL_KEY });
      await SecureStoragePlugin.get({ key: this.BIOMETRIC_PASS_KEY });
      return true;
    } catch { return false; }
  }

  // Botón "Ingresar con biometría"
  async biometricPasswordLogin(): Promise<void> {
    if (!this.isNative()) throw new Error('Solo disponible en app nativa');
    // Requiere que el usuario lo haya habilitado
    if (localStorage.getItem(this.BIOMETRIC_ENABLED) !== '1') throw new Error('Biometría no habilitada');

    // Prompt biométrico
    await BiometricAuth.authenticate({
      reason: 'Ingresar con biometría',
      iosFallbackTitle: 'Usar código',
      cancelTitle: 'Cancelar',
      allowDeviceCredential: true,
    });

    // Leer email/clave del secure storage
    const email = (await SecureStoragePlugin.get({ key: this.BIOMETRIC_EMAIL_KEY })).value;
    const clave = (await SecureStoragePlugin.get({ key: this.BIOMETRIC_PASS_KEY })).value;

    // Reusa tu login normal (WEB o NATIVO)
    await this.login(email, clave);
  }

  async isBiometricsAvailable(): Promise<boolean> {
    if (!this.isNative()) return false;
    try {
      const info = await BiometricAuth.checkBiometry();
      return info.isAvailable || info.deviceIsSecure;
    } catch {
      return false;
    }
  }

  // (Opcional) deshabilitar biometría
  async disableBiometrics(): Promise<void> {
    if (this.isNative()) {
      try { await SecureStoragePlugin.remove({ key: this.BIOMETRIC_EMAIL_KEY }); } catch {}
      try { await SecureStoragePlugin.remove({ key: this.BIOMETRIC_PASS_KEY  }); } catch {}
    }
    localStorage.removeItem(this.BIOMETRIC_ENABLED);
  }

  async enableBiometrics(enable: boolean): Promise<void> {
    localStorage.setItem(this.BIOMETRIC_ENABLED, enable ? '1' : '0');
  }

  async biometricsEnabled(): Promise<boolean> {
    return localStorage.getItem(this.BIOMETRIC_ENABLED) === '1';
  }

  /** Devuelve 'face' | 'finger' | null (normaliza número/string/array) y cachea */
  async getBiometryKind(): Promise<'face' | 'finger' | null> {
    try {
      if (Capacitor.getPlatform() === 'web') return null;

      // cache simple
      const cached = localStorage.getItem('biometryKind');
      if (cached === 'face' || cached === 'finger') return cached as any;

      const info = await BiometricAuth.checkBiometry();
      if (!info?.isAvailable) return null;

      // Normalizar a string 'face' | 'finger' teniendo en cuenta:
      // - info.biometryType puede ser number o string
      // - info.biometryTypes puede ser array de numbers
      const rawSingle = (info as any).biometryType;
      const rawList = (info as any).biometryTypes as any[] | undefined;

      const pickFromNumber = (n: number | undefined): 'face' | 'finger' | null => {
        if (n === undefined || n === null) return null;
        // Convención de este plugin:
        // 1 => TouchID / Fingerprint, 2 => FaceID
        if (Number(n) === 2) return 'face';
        if (Number(n) === 1) return 'finger';
        return null;
      };

      const pickFromString = (s: string | undefined): 'face' | 'finger' | null => {
        if (!s) return null;
        const t = s.toLowerCase();
        if (/(face|faceid|face-id)/.test(t)) return 'face';
        if (/(touch|touchid|finger|fingerprint)/.test(t)) return 'finger';
        return null;
      };

      let kind: 'face' | 'finger' | null = null;

      // 1) Prioridad: array si viene poblado (por si hay múltiples tipos)
      if (Array.isArray(rawList) && rawList.length) {
        // si existe FaceID en la lista, preferilo
        kind = pickFromNumber(rawList.find(n => Number(n) === 2));
        if (!kind) kind = pickFromNumber(rawList[0]);
      }

      // 2) Si no salió de la lista, probá el single
      if (!kind) {
        if (typeof rawSingle === 'number') kind = pickFromNumber(rawSingle);
        else if (typeof rawSingle === 'string') kind = pickFromString(rawSingle);
      }

      // 3) Último fallback iOS: si hay biometría pero no vino tipo, asumí FaceID
      if (!kind && Capacitor.getPlatform() === 'ios' && info.isAvailable) {
        kind = 'face';
      }

      if (kind) localStorage.setItem('biometryKind', kind);
      return kind;
    } catch {
      return null;
    }
  }
}